import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useRunbooksStore } from '@/stores/runbooksStore';
import { api } from '@/lib/api';
import type { ServiceDto } from '@/types/api';
import { Plus, Trash2, GripVertical, ArrowLeft, BookOpen } from 'lucide-react';
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Checkbox, Input, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Textarea, Form, FormControl, FormField, FormItem, FormLabel, FormMessage, useToast } from '@nimbitlabs/ui';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { runbookFormSchema, type RunbookFormData } from '@/schemas/runbookSchema';
import { Runbook } from '@/lib/runbooks';

export const EditRunbook: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { getRunbook, updateRunbook } = useRunbooksStore();
  const [services, setServices] = useState<ServiceDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [runbook, setRunbook] = useState<Runbook | null>(null);

  const form = useForm<RunbookFormData>({
    resolver: zodResolver(runbookFormSchema),
    defaultValues: {
      name: '',
      description: '',
      serviceId: '',
      trigger: '',
      steps: [{ title: '', description: '', action: '', automated: false }],
    },
    mode: 'onBlur',
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'steps',
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        // Load services
        const servicesData = await api.services.getAll();
        setServices(servicesData);

        // Fetch runbook with steps from store
        if (id) {
          const foundRunbook = await getRunbook(id);
          if (foundRunbook) {
            setRunbook(foundRunbook);
            // Set form values
            form.reset({
              name: foundRunbook.name,
              description: foundRunbook.description,
              serviceId: foundRunbook.serviceId,
              trigger: foundRunbook.trigger,
              steps: foundRunbook.steps && foundRunbook.steps.length > 0
                ? foundRunbook.steps.map(step => ({
                    title: step.title,
                    description: step.description || '',
                    action: step.action || '',
                    automated: step.automated,
                  }))
                : [{ title: '', description: '', action: '', automated: false }],
            });
          }
        }
      } catch (error) {
        console.error('Failed to load data:', error);
        toast({
          title: 'Error',
          description: 'Failed to load runbook',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [id, getRunbook, form, toast]);

  const onSubmit = async (data: RunbookFormData) => {
    if (!id) return;
    
    try {
      const payload = {
        name: data.name,
        description: data.description,
        serviceId: data.serviceId,
        trigger: data.trigger,
        steps: data.steps.map((step, index) => ({
          order: index + 1,
          title: step.title,
          description: step.description || '',
          action: step.action || '',
          actionConfig: null,
          automated: step.automated,
        })),
      };

      await updateRunbook(id, payload);
      toast({
        title: 'Success',
        description: 'Runbook updated successfully',
      });
      navigate('/runbooks');
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update runbook',
        variant: 'destructive',
      });
    }
  };

  const addStep = () => {
    append({ title: '', description: '', action: '', automated: false });
  };

  const removeStep = (index: number) => {
    if (fields.length > 1) {
      remove(index);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" asChild>
            <Link to="/runbooks">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Runbooks
            </Link>
          </Button>
        </div>
        <Card>
          <CardContent className="text-center py-12">
            <BookOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4 animate-pulse" />
            <h3 className="text-lg font-medium mb-2">Loading runbook...</h3>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!runbook) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" asChild>
            <Link to="/runbooks">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Runbooks
            </Link>
          </Button>
        </div>
        <Card>
          <CardContent className="text-center py-12">
            <h3 className="text-lg font-medium mb-2">Runbook not found</h3>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="sm" asChild>
          <Link to="/runbooks">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Runbooks
          </Link>
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5" />
            Edit Runbook
          </CardTitle>
          <CardDescription>
            Update runbook information and steps
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <div className="space-y-4">
                {/* Name */}
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Name <span className="text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="e.g., Database Restart Procedure"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Description */}
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Description <span className="text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          placeholder="Describe what this runbook does..."
                          rows={3}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Service (Required) */}
                <FormField
                  control={form.control}
                  name="serviceId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Service <span className="text-destructive">*</span>
                      </FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a service" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {services.map((service) => (
                            <SelectItem key={service.id} value={service.id}>
                              {service.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        The service this runbook applies to (required)
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Trigger */}
                <FormField
                  control={form.control}
                  name="trigger"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Trigger <span className="text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="e.g., High CPU usage, Database connection errors"
                        />
                      </FormControl>
                      <p className="text-xs text-muted-foreground">
                        When should this runbook be executed?
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Steps */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>
                      Steps <span className="text-destructive">*</span>
                    </Label>
                    <Button type="button" variant="outline" size="sm" onClick={addStep}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Step
                    </Button>
                  </div>

                  <div className="space-y-3">
                    {fields.map((field, index) => (
                      <Card key={field.id}>
                        <CardContent className="pt-4">
                          <div className="space-y-3">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex items-center gap-2">
                                <GripVertical className="h-4 w-4 text-muted-foreground" />
                                <span className="text-sm font-medium">Step {index + 1}</span>
                              </div>
                              {fields.length > 1 && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeStep(index)}
                                >
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              )}
                            </div>

                            <div className="space-y-2">
                              <FormField
                                control={form.control}
                                name={`steps.${index}.title`}
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel className="text-xs text-muted-foreground">
                                      Title <span className="text-destructive">*</span>
                                    </FormLabel>
                                    <FormControl>
                                      <Input {...field} placeholder="Step title" />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={form.control}
                                name={`steps.${index}.description`}
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel className="text-xs text-muted-foreground">Description</FormLabel>
                                    <FormControl>
                                      <Textarea {...field} placeholder="Step description (optional)" rows={2} />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={form.control}
                                name={`steps.${index}.action`}
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel className="text-xs text-muted-foreground">Action</FormLabel>
                                    <FormControl>
                                      <Input {...field} placeholder="e.g., restart, scale (optional)" />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={form.control}
                                name={`steps.${index}.automated`}
                                render={({ field }) => (
                                  <FormItem className="flex items-center space-x-2 space-y-0">
                                    <FormControl>
                                      <Checkbox
                                        checked={field.value}
                                        onCheckedChange={field.onChange}
                                      />
                                    </FormControl>
                                    <FormLabel className="text-sm font-normal">
                                      Automated step (can be executed automatically)
                                    </FormLabel>
                                  </FormItem>
                                )}
                              />
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => navigate('/runbooks')}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={form.formState.isSubmitting}
                >
                  {form.formState.isSubmitting ? 'Updating...' : 'Update Runbook'}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
};
